'use client';

import React, { HTMLAttributes, useCallback, useEffect, useState } from 'react';

import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { ShortListExperienceItem, ShortListVillaItem } from './ShortListItem';
import { ShortListButton } from './ShortListButton';
import { IExperience } from '@/types/Experience';
import { IVilla } from '@/types/Villa';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { Locale } from '../../../i18n.config';
import { ISendToFriendsFormDictionary } from '@/dictionaries/components/SendToFriendsForm/types';
import useLocalStorage from '@/hooks/useLocalStorage';

interface IShortListModalContentProps extends HTMLAttributes<HTMLDivElement> {
  dict: IModalsDictionary;
  simpleButton?: boolean;
  enquiryFormDict: IEnquiryFormDictionary;
  sendToFriendFormDict: ISendToFriendsFormDictionary;
  countriesDict: ICountriesDictionary;
  datePickerDict: IDatePickerDictonary;
  lang: Locale;
}

const ShortListModalContent: React.FC<IShortListModalContentProps> = ({
  dict,
  lang,
  enquiryFormDict,
  sendToFriendFormDict,
  countriesDict,
  datePickerDict,
  simpleButton = false,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [villaShortList, setVillaShortList] = useLocalStorage(
    'villas',
    [] as IVilla[],
  );
  const [experienceShortList, setExperienceShortList] = useLocalStorage(
    'experience',
    [] as IExperience[],
  );

  const handleRemoveShortItemFromList = useCallback(
    (itemType: string, id: number | string) => {
      if (typeof window !== 'undefined') {
        if (itemType === 'villa') {
          const updatedFavourites = villaShortList.filter(
            (item) => item.title !== id,
          );
          setVillaShortList(updatedFavourites);
        }
        if (itemType === 'experience') {
          const updatedFavourites = experienceShortList.filter(
            (item) => item.id !== id,
          );
          setExperienceShortList(updatedFavourites);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [villaShortList, experienceShortList],
  );

  return (
    <ShortListButton
      simpleButton={simpleButton}
      villaFooterIsHiden={villaShortList.length === 0}
      experienceFooterIsHiden={experienceShortList.length === 0}
      shortListLength={villaShortList.length + experienceShortList.length}
      lang={lang}
      hasVillas={villaShortList.length > 0}
      hasExperience={experienceShortList.length > 0}
      enquiryFormDict={enquiryFormDict}
      sendToFriendFormDict={sendToFriendFormDict}
      countriesDict={countriesDict}
      datePickerDict={datePickerDict}
      modalsDict={dict}
      dict={dict.shortListModal}
      modalIsOpen={modalIsOpen}
      setModalIsOpen={setModalIsOpen}
      villasShortListModalContent={
        <>
          {villaShortList.length > 0 ? (
            villaShortList.map((villa, index) => (
              <ShortListVillaItem
                lang={lang}
                itemRemove={handleRemoveShortItemFromList}
                dict={dict.shortListModal}
                key={index}
                villa={villa}
                setModalIsOpen={setModalIsOpen}
              />
            ))
          ) : (
            <h2 className="w-full text-center font-title text-[22px] font-semibold mt-16 text-white">
              {dict.shortListModal.emptyVillas}
            </h2>
          )}
        </>
      }
      experiencesShortListModalContent={
        <>
          {experienceShortList.length > 0 &&
            experienceShortList.map((experience, index) => (
              <ShortListExperienceItem
                lang={lang}
                itemRemove={handleRemoveShortItemFromList}
                dict={dict.shortListModal}
                key={index}
                experience={experience}
                setModalIsOpen={setModalIsOpen}
              />
            ))}
        </>
      }
    />
  );
};

export default ShortListModalContent;
